import { useEffect, useRef } from 'react';

import EventSource, {
  ErrorEvent,
  ExceptionEvent,
  MessageEvent,
  TimeoutEvent,
} from 'react-native-sse';

import { useIdle } from '@mantine/hooks';
import ErrorService from '../services/ErrorService';
import { getAuthCookie } from '../utils/AuthUtils';
import { useRefresh } from './useRefresh.ts';

const IDLE_TIMEOUT = 1000 * 60 * 10; // 10mins

type UseEventSourceProps = {
  url: string;
  maxRetries?: number;
  skipIdle?: boolean;
  idleTimeout?: number;
  enabled?: boolean;
  onMessage: (evt: MessageEvent) => any;
  onError?: (evt: ErrorEvent | TimeoutEvent | ExceptionEvent) => any;
};

const useEventSource = (props: UseEventSourceProps) => {
  const { url, skipIdle, idleTimeout, enabled = true, maxRetries = 3 } = props;

  const { onMessage, onError } = useRef({
    onMessage: props.onMessage,
    onError: props.onError,
  }).current;

  const retryCount = useRef(0);

  const idle = useIdle(idleTimeout ?? IDLE_TIMEOUT, { initialState: false });

  const { key, refresh } = useRefresh();

  useEffect(() => {
    if (!key) {
      // key required to trigger effect for reconnection
      return;
    }

    if (!enabled || (!skipIdle && idle)) {
      return;
    }

    const es = new EventSource(url, {
      headers: {
        Authorization: `Bearer ${getAuthCookie()}`,
      },
      pollingInterval: 10, // auto reconnect
    });

    es.addEventListener('message', onMessage);

    es.addEventListener('error', (evt) => {
      onError?.(evt);
      es.close();

      const err = evt as ErrorEvent;

      if (err.xhrStatus === 504 || err.xhrStatus === 0) {
        refresh();
        return;
      }

      if (err.xhrStatus !== 403) {
        ErrorService.notify(
          'Failed to connect to SSE',
          new Error(err.message ?? 'Unknown error'),
        );
        if (retryCount.current < maxRetries) {
          retryCount.current += 1;
          refresh();
        }
      }
    });

    return () => {
      es.close();
      es.removeAllEventListeners();
    };
  }, [
    onError,
    onMessage,
    url,
    idle,
    skipIdle,
    enabled,
    key,
    refresh,
    maxRetries,
  ]);
};

export default useEventSource;
